'use client';

import React, { SyntheticEvent } from 'react';
import { AllServices as IAllServices } from '@sapientpro/sapientpro-data-models';
import { MouseParallax } from 'react-just-parallax';
import { useSetAtom } from 'jotai';
import classNames from 'classnames';
import styles from './AllServices.module.scss';
import ContactModal from '../../components/ContactModal';
import AnimatedTitle from '../../components/AnimatedTitle';
import ServiceBlock from './ServiceBlock';
import Industries from './Industries';
import Facts from './Facts';
import TextReviewCard from './TextReviewCard';
import GradientCircles from '../../components/GradientCircles';
import Ellipse from '../../components/Ellipse';
import HeroImages from './HeroImages';
import Button, { ButtonVariants } from '../../components/Button';
import contactModalTriggerButton from '../../store/contactForm';
import { GAEmit, GAEvents } from '../../gtagHelper';
import { EllipseVariants } from '../../../constants';

interface IProps {
  data: IAllServices;
}

const AllServices = ({ data }: IProps) => {
  const setContactsModalTrigger = useSetAtom(contactModalTriggerButton);

  const handleJoinUsClick = (event: SyntheticEvent) => {
    setContactsModalTrigger(`${(event.target as HTMLElement).innerText} | All Services`);
    GAEmit(GAEvents.joinUsClick);
  };

  return (
    <main className={styles.AllServices}>
      <section className={styles.hero}>
        <HeroImages />
        <h1 className={classNames('sectionTitle', styles.hero__title)}>
          <AnimatedTitle title={data.title} />
        </h1>
        <p className={styles.hero__text}>{data.description}</p>
        <Button
          className={styles.ctaButton}
          variant={ButtonVariants.PRIMARY}
          onClick={handleJoinUsClick}
          icon={(
            <svg>
              <use
                xlinkHref='/media/icon-nest.svg#iconNest'
                href='/media/icon-nest.svg#iconNest'
              />
            </svg>
          )}
          iconPosition='right'
          iconSize={{ width: 24, height: 24 }}
        >
          {data.ctaButtonContent}
        </Button>
        <Facts facts={data.facts} />
      </section>
      <section className={styles.content}>
        <ServiceBlock servicesBlock={data.servicesBlock} />
        <TextReviewCard textReview={data.textReviews} />
        <Industries industries={data?.industryTiles} />

        <div className={styles.parallax}>
          <MouseParallax
            isAbsolutelyPositioned
            strength={0.12}
          >
            <Ellipse
              variant={EllipseVariants.NOISE}
              size={94}
              className={styles.parallax__industries__noise}
            />
          </MouseParallax>
          <MouseParallax
            isAbsolutelyPositioned
            strength={0.05}
          >
            <Ellipse
              variant={EllipseVariants.DRAW}
              size={600}
              className={styles.parallax__draw}
            />
          </MouseParallax>
          <MouseParallax
            isAbsolutelyPositioned
            strength={0.12}
          >
            <Ellipse
              variant={EllipseVariants.NOISE}
              size={94}
              className={styles.parallax__noise}
            />
          </MouseParallax>
          <MouseParallax
            isAbsolutelyPositioned
            strength={0.08}
          >
            <Ellipse
              variant={EllipseVariants.GRADIENT}
              size={40}
              className={styles.parallax__gradient}
            />
          </MouseParallax>
        </div>
      </section>
      <div
        id='contact-form'
        className={styles.contactContainer}
      >
        <ContactModal
          isBlockView
          titleComponent='h3'
          customModalClassName={styles.contact}
        />
      </div>
      <GradientCircles />
    </main>
  );
};

export default AllServices;
